import { useCallback } from "react";
import { useIntl } from "react-intl";
import { ExtraCategory } from "../../../domain/ExtraCategory.enum";
import { IssueCode, ValidationIssue } from "../../../domain/entities/ValidationIssue";
import { useSaveWarningsDialog } from "../SaveWarningsDialog";
import { extraSaveWarningsMessages } from "./extraSaveWarnings.messages";

/** A successful CreateExtra / UpdateExtra, as far as its warnings are concerned. */
export interface ExtraSaveOutcome {
  /** *Extras with categories* of the current property; off, the request carried no category. */
  readonly categoriesActive: boolean;
  readonly category: ExtraCategory;
  /** AGR children with at least one inventory line selected in *Apply to* (the parent excluded). */
  readonly propagatedTo: number;
  readonly warnings: readonly ValidationIssue[];
}

/** The children, by display name (ticker when the backend sends no name), with the flag off. */
const disabledProperties = (warnings: readonly ValidationIssue[]): string[] =>
  warnings
    .filter(warning => warning.is(IssueCode.EXTRA_CATEGORY_NOT_ENABLED_IN_PROPERTY))
    .map(warning => warning.param("property") || warning.target.propertyTicker || "")
    .filter(name => name !== "");

/**
 * The warnings of saving an extra. `showSaveWarnings` resolves when the user accepts, or at once
 * when there is nothing to tell; render `SaveWarningsDialog` with `dialog`. Today it covers one
 * case: an AGR parent propagated a category to children that have *Extras with categories* off.
 */
export function useExtraSaveWarnings() {
  const intl = useIntl();
  const { showSummary, dialog } = useSaveWarningsDialog();

  const showSaveWarnings = useCallback(
    async ({ categoriesActive, category, propagatedTo, warnings }: ExtraSaveOutcome) => {
      if (!categoriesActive || category === ExtraCategory.NONE) {
        return;
      }
      const properties = disabledProperties(warnings);
      if (properties.length === 0) {
        return;
      }
      await showSummary({
        title: intl.formatMessage(extraSaveWarningsMessages.title),
        summary: intl.formatMessage(extraSaveWarningsMessages.agrCategoryNotEnabled, {
          // Never below the warned children, in case the count and the backend disagree.
          total: Math.max(propagatedTo, properties.length),
          disabled: properties.length,
        }),
        items: properties,
      });
    },
    [intl, showSummary]
  );

  return { showSaveWarnings, dialog };
}
